import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import {
  XMarkIcon,
  TruckIcon,
  MapPinIcon,
  UsersIcon,
  PencilIcon,
  QrCodeIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { Trip, TripBus, Driver } from '../../types';
import { DateDisplay } from '../../components/Forms';

interface TripDetailsProps {
  tripId: string;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (trip: Trip) => void;
}

const STATUS_MAP: Record<string, { label: string; color: string }> = {
  draft: { label: 'مسودة', color: 'bg-gray-100 text-gray-700' },
  published: { label: 'منشورة', color: 'bg-blue-50 text-blue-800' },
  full: { label: 'مكتملة العدد', color: 'bg-amber-50 text-amber-800' },
  ongoing: { label: 'جارية', color: 'bg-emerald-50 text-emerald-800' },
  completed: { label: 'مكتملة', color: 'bg-violet-50 text-violet-800' },
  cancelled: { label: 'ملغية', color: 'bg-red-50 text-red-800' },
};

const busStatusLabel = (status?: string) => {
  if (status === 'on-trip') return 'في رحلة';
  if (status === 'maintenance') return 'صيانة';
  if (status === 'out-of-service') return 'خارج الخدمة';
  return 'متاح';
};

const TripDetails: React.FC<TripDetailsProps> = ({ tripId, isOpen, onClose, onEdit }) => {
  const location = useLocation();
  const boardingHref = `${location.pathname.startsWith('/vendor') ? '/vendor' : ''}/boarding?tripId=${tripId}`;
  const [driverWaBusy, setDriverWaBusy] = useState<string | null>(null);
  const [driverWaMsg, setDriverWaMsg] = useState('');

  const { data: trip, isLoading, error } = useQuery({
    queryKey: ['trip', tripId],
    queryFn: () => apiService.getTrip(tripId),
    enabled: isOpen && !!tripId,
  });

  const buses = useMemo((): TripBus[] => {
    if (!trip) return [];
    if (trip.allBuses?.length) return trip.allBuses;
    if (trip.buses?.length) return trip.buses;
    if (trip.bus) {
      const fallbackDriver: Driver = {
        name: trip.driver?.name || '',
        phone: trip.driver?.phone || '',
        licenseNumber: trip.driver?.licenseNumber || '',
        licenseExpiry: trip.driver?.licenseExpiry || new Date(0),
      };
      return [
        {
          bus: trip.bus,
          driver: fallbackDriver,
          supervisor: trip.supervisor,
          passengers: trip.passengers,
        },
      ];
    }
    return [];
  }, [trip]);

  const passengers = useMemo(() => {
    if (!trip) return [];
    const fromBuses = (trip.buses || []).flatMap((b) => b.passengers || []);
    const legacy = trip.passengers || [];
    return [...legacy, ...fromBuses];
  }, [trip]);

  const capacity = buses.reduce((sum, b) => sum + (b.bus?.capacity || 0), 0);
  const passengerCount = trip?.totalPassengers ?? passengers.length;

  const handleNotifyDriver = async (busIndex?: number) => {
    if (!tripId) return;
    setDriverWaBusy(busIndex === undefined ? 'all' : String(busIndex));
    setDriverWaMsg('');
    try {
      const res = await apiService.notifyTripDriver(tripId, busIndex);
      setDriverWaMsg(res.message || 'تم إرسال واتساب للسائق');
    } catch (err: any) {
      setDriverWaMsg(
        err?.response?.data?.message || err?.message || 'فشل إرسال واتساب للسائق'
      );
    } finally {
      setDriverWaBusy(null);
    }
  };

  if (!isOpen) return null;

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-xl p-8">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto" />
          <p className="mt-4 text-gray-600 text-center text-sm">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (error || !trip) {
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full text-center">
          <p className="text-gray-900 font-medium">تعذر تحميل تفاصيل الرحلة</p>
          <button type="button" onClick={onClose} className="mt-4 btn-primary">
            إغلاق
          </button>
        </div>
      </div>
    );
  }

  const statusInfo = STATUS_MAP[trip.status] || {
    label: trip.status,
    color: 'bg-gray-100 text-gray-700',
  };

  const expenses = trip.expenses || ({} as Trip['expenses']);
  const revenue = trip.revenue || ({} as Trip['revenue']);
  const hasFinance =
    (expenses.total || 0) > 0 ||
    (revenue.total || 0) > 0 ||
    (expenses.fuel || 0) > 0 ||
    (revenue.ticketSales || 0) > 0;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-100">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-semibold text-gray-900">{trip.tripNumber}</h2>
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusInfo.color}`}
              >
                {statusInfo.label}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {trip.date ? (
                <>
                  التاريخ: <DateDisplay value={trip.date} className="inline-flex" />
                </>
              ) : (
                'بدون تاريخ'
              )}
              <span className="mx-2 text-gray-300">|</span>
              {passengerCount} راكب
              {capacity > 0 ? ` / ${capacity}` : ''}
              <span className="mx-2 text-gray-300">|</span>
              {buses.length || 1} باص
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => handleNotifyDriver()}
              disabled={driverWaBusy !== null}
              className="inline-flex items-center gap-1.5 text-sm px-2.5 py-1.5 rounded-lg text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
              title="إرسال واتساب لكل السائقين"
            >
              <ChatBubbleLeftRightIcon className="w-4 h-4" />
              <span className="hidden sm:inline">
                {driverWaBusy === 'all' ? 'جاري الإرسال...' : 'واتساب السائقين'}
              </span>
            </button>
            <Link
              to={boardingHref}
              onClick={onClose}
              className="inline-flex items-center gap-1.5 text-sm px-2.5 py-1.5 rounded-lg text-indigo-700 hover:bg-indigo-50"
              title="بوابة الصعود"
            >
              <QrCodeIcon className="w-4 h-4" />
              <span className="hidden sm:inline">صعود</span>
            </Link>
            <button
              type="button"
              onClick={() => onEdit(trip)}
              className="inline-flex items-center gap-1.5 text-sm px-2.5 py-1.5 rounded-lg text-gray-700 hover:bg-gray-50"
              title="تعديل"
            >
              <PencilIcon className="w-4 h-4" />
              <span className="hidden sm:inline">تعديل</span>
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-50"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto p-5 space-y-6">
          {driverWaMsg && (
            <p
              className={`text-sm rounded-lg px-3 py-2 ${
                driverWaMsg.includes('فشل') || driverWaMsg.includes('لا يوجد')
                  ? 'bg-red-50 text-red-700'
                  : 'bg-emerald-50 text-emerald-800'
              }`}
            >
              {driverWaMsg}
            </p>
          )}

          {/* Routes */}
          <section>
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <MapPinIcon className="w-4 h-4 text-gray-500" />
              المسار
            </h3>
            {trip.routes?.length ? (
              <ul className="space-y-2">
                {trip.routes.map((route, index) => (
                  <li
                    key={index}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 text-sm py-2 border-b border-gray-50 last:border-0"
                  >
                    <div className="font-medium text-gray-900">
                      {route.from}
                      <span className="text-gray-400 mx-2">←</span>
                      {route.to}
                    </div>
                    <div className="text-gray-500 text-xs sm:text-sm">
                      <DateDisplay value={route.dateFrom} className="inline-flex" />
                      {route.timeFrom ? ` · ${route.timeFrom}` : ''}
                      {route.dateTo && route.dateTo !== route.dateFrom && (
                        <>
                          {' → '}
                          <DateDisplay value={route.dateTo} className="inline-flex" />
                          {route.timeTo ? ` · ${route.timeTo}` : ''}
                        </>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : trip.route ? (
              <p className="text-sm text-gray-800">
                <span className="font-medium">
                  {trip.route.from} ← {trip.route.to}
                </span>
              </p>
            ) : (
              <p className="text-sm text-gray-500">لا يوجد مسار</p>
            )}
          </section>

          {/* Buses */}
          <section>
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <TruckIcon className="w-4 h-4 text-gray-500" />
              الحافلات
            </h3>
            {buses.length === 0 ? (
              <p className="text-sm text-gray-500">لا توجد حافلات</p>
            ) : (
              <div className="space-y-3">
                {buses.map((busData, index) => {
                  const staff = busData.boardingStaff;
                  const staffName =
                    staff && typeof staff === 'object'
                      ? staff.fullName || staff.username
                      : null;
                  return (
                    <div
                      key={busData.bus?._id || index}
                      className="rounded-lg border border-gray-200 p-3 text-sm"
                    >
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <p className="font-semibold text-gray-900">
                          {busData.bus?.busNumber || `باص ${index + 1}`}
                          {busData.bus?.plateNumber && (
                            <span className="font-normal text-gray-500 mr-2">
                              · {busData.bus.plateNumber}
                            </span>
                          )}
                        </p>
                        <span className="text-xs text-gray-500">
                          {busData.bus?.capacity || 0} مقعد ·{' '}
                          {busStatusLabel(busData.bus?.status)}
                        </span>
                      </div>
                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-gray-600">
                        {busData.driver?.name && (
                          <p>
                            السائق: <span className="text-gray-900">{busData.driver.name}</span>
                            {busData.driver.phone ? ` · ${busData.driver.phone}` : ''}
                          </p>
                        )}
                        {busData.supervisor?.name && (
                          <p>
                            المشرف:{' '}
                            <span className="text-gray-900">{busData.supervisor.name}</span>
                            {busData.supervisor.phone
                              ? ` · ${busData.supervisor.phone}`
                              : ''}
                          </p>
                        )}
                        {staffName && (
                          <p>
                            موظف الصعود: <span className="text-gray-900">{staffName}</span>
                          </p>
                        )}
                      </div>
                      {busData.driver?.phone && (
                        <div className="mt-3">
                          <button
                            type="button"
                            onClick={() => handleNotifyDriver(index)}
                            disabled={driverWaBusy !== null}
                            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-emerald-200 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                          >
                            <ChatBubbleLeftRightIcon className="w-3.5 h-3.5" />
                            {driverWaBusy === String(index)
                              ? 'جاري الإرسال...'
                              : 'إرسال واتساب للسائق'}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Passengers */}
          {passengers.length > 0 && (
            <section>
              <div className="flex items-center justify-between mb-3 gap-2">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <UsersIcon className="w-4 h-4 text-gray-500" />
                  الركاب ({passengers.length})
                </h3>
                <Link
                  to={boardingHref}
                  onClick={onClose}
                  className="text-xs text-indigo-600 hover:underline"
                >
                  صفحة الصعود
                </Link>
              </div>
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-gray-600 text-xs">
                      <th className="px-3 py-2 text-right font-medium">الاسم</th>
                      <th className="px-3 py-2 text-right font-medium">المقعد</th>
                      <th className="px-3 py-2 text-right font-medium">الجوال</th>
                      <th className="px-3 py-2 text-right font-medium">الصعود</th>
                    </tr>
                  </thead>
                  <tbody>
                    {passengers.map((passenger, index) => (
                      <tr key={index} className="border-t border-gray-100">
                        <td className="px-3 py-2 text-gray-900">{passenger.passengerName}</td>
                        <td className="px-3 py-2 text-gray-700">{passenger.seatNumber || '—'}</td>
                        <td className="px-3 py-2 text-gray-700">{passenger.phone || '—'}</td>
                        <td className="px-3 py-2">
                          <span
                            className={`text-xs font-medium ${
                              passenger.boardingStatus === 'boarded'
                                ? 'text-emerald-700'
                                : passenger.boardingStatus === 'no-show'
                                  ? 'text-red-600'
                                  : 'text-amber-700'
                            }`}
                          >
                            {passenger.boardingStatus === 'boarded'
                              ? 'صعد'
                              : passenger.boardingStatus === 'no-show'
                                ? 'لم يحضر'
                                : 'مؤكد'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Finance — compact, only if data exists */}
          {hasFinance && (
            <section>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">المالية</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="space-y-1.5">
                  <p className="text-xs text-gray-500 mb-1">مصروفات</p>
                  {(expenses.fuel || 0) > 0 && (
                    <div className="flex justify-between text-gray-700">
                      <span>وقود</span>
                      <span>{expenses.fuel} ر.س</span>
                    </div>
                  )}
                  {(expenses.tolls || 0) > 0 && (
                    <div className="flex justify-between text-gray-700">
                      <span>رسوم</span>
                      <span>{expenses.tolls} ر.س</span>
                    </div>
                  )}
                  {(expenses.meals || 0) > 0 && (
                    <div className="flex justify-between text-gray-700">
                      <span>وجبات</span>
                      <span>{expenses.meals} ر.س</span>
                    </div>
                  )}
                  {(expenses.accommodation || 0) > 0 && (
                    <div className="flex justify-between text-gray-700">
                      <span>إقامة</span>
                      <span>{expenses.accommodation} ر.س</span>
                    </div>
                  )}
                  {(expenses.maintenance || 0) > 0 && (
                    <div className="flex justify-between text-gray-700">
                      <span>صيانة</span>
                      <span>{expenses.maintenance} ر.س</span>
                    </div>
                  )}
                  {(expenses.other || 0) > 0 && (
                    <div className="flex justify-between text-gray-700">
                      <span>أخرى</span>
                      <span>{expenses.other} ر.س</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold text-gray-900 pt-1 border-t border-gray-100">
                    <span>الإجمالي</span>
                    <span>{expenses.total || 0} ر.س</span>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <p className="text-xs text-gray-500 mb-1">إيرادات</p>
                  {(revenue.ticketSales || 0) > 0 && (
                    <div className="flex justify-between text-gray-700">
                      <span>تذاكر</span>
                      <span>{revenue.ticketSales} ر.س</span>
                    </div>
                  )}
                  {(revenue.accommodationFees || 0) > 0 && (
                    <div className="flex justify-between text-gray-700">
                      <span>إقامة</span>
                      <span>{revenue.accommodationFees} ر.س</span>
                    </div>
                  )}
                  {(revenue.additionalServices || 0) > 0 && (
                    <div className="flex justify-between text-gray-700">
                      <span>خدمات</span>
                      <span>{revenue.additionalServices} ر.س</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold text-gray-900 pt-1 border-t border-gray-100">
                    <span>الإجمالي</span>
                    <span>{revenue.total || 0} ر.س</span>
                  </div>
                  <div className="flex justify-between font-semibold text-emerald-700">
                    <span>صافي</span>
                    <span>
                      {(revenue.total || 0) - (expenses.total || 0)} ر.س
                    </span>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Notes */}
          {trip.notes?.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">ملاحظات</h3>
              <ul className="space-y-2">
                {trip.notes.map((note, index) => (
                  <li key={index} className="text-sm text-gray-700 border-r-2 border-gray-200 pr-3">
                    <p>{note.text}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      <DateDisplay value={note.createdAt} showTime className="inline-flex" />
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-100 flex justify-end">
          <button type="button" onClick={onClose} className="btn-secondary text-sm">
            إغلاق
          </button>
        </div>
      </div>
    </div>
  );
};

export default TripDetails;
